/**
 * ShareEmbedCard.tsx
 * UI card for generating and copying embed code for the accessibility passport widget
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Code, Copy, Check } from 'lucide-react';
import { generateEmbedCode } from '../../lib/embed/generateEmbedCode';
import { toast } from 'sonner';

interface ShareEmbedCardProps {
  structureId: string;
  portal?: string;
  theme?: 'blue' | 'brand' | 'orange';
  language?: 'it' | 'en' | 'de' | 'fr';
  size?: 'compact' | 'standard' | 'detailed';
}

export function ShareEmbedCard({ structureId, portal, theme = 'blue', language = 'it', size = 'standard' }: ShareEmbedCardProps) {
  const [embedMode, setEmbedMode] = useState<'iframe'>('iframe');
  const [responsive, setResponsive] = useState(true);
  const [autoHeight, setAutoHeight] = useState(true);
  const [heightPx, setHeightPx] = useState(760);
  const [embedCode, setEmbedCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(false);

  /**
   * Get base URL from environment or fallback to sensible defaults
   * Priority:
   * 1. VITE_B2B_BASE_URL (explicitly set in .env.local)
   * 2. window.location.origin (current origin in browser)
   * 3. http://localhost:3004 (dev server default)
   * 
   * Set VITE_B2B_BASE_URL in .env.local for production deployments
   */
  const getBaseUrl = () => {
    // 1. Use explicit B2B base URL from environment if set
    const envBaseUrl = import.meta.env.VITE_B2B_BASE_URL;
    if (envBaseUrl) return envBaseUrl;
    
    // 2. Fallback to current origin in browser context
    if (typeof window !== 'undefined') {
      return window.location.origin;
    }
    
    // 3. Final fallback for dev server
    return 'http://localhost:3004';
  };

  // Generate embed code whenever settings change
  useEffect(() => {
    const code = generateEmbedCode({
      baseUrl: getBaseUrl(),
      structureId,
      portal,
      theme,
      language,
      size,
      version: '1',
      heightPx: autoHeight ? 760 : heightPx, // Use as fallback when autoHeight is on
      responsive,
      autoHeight,
      iframeId: 'travelin-passport-iframe',
    });
    setEmbedCode(code);
  }, [structureId, portal, theme, language, size, responsive, autoHeight, heightPx]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopied(true);
      toast.success('Codice copiato negli appunti!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Errore durante la copia del codice');
      console.error('Failed to copy:', err);
    }
  };

  const handleShare = async () => {
    if (sharing) return;
    setSharing(true);
    try {
      const baseUrl = getBaseUrl();
      const cleanBaseUrl = String(baseUrl).replace(/\/$/, '');
      const shareUrl = `${cleanBaseUrl}/?structureId=${encodeURIComponent(structureId)}`;

      if (typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share({
          title: 'Accessibility Passport',
          text: 'Consulta il profilo di accessibilità della struttura',
          url: shareUrl,
        });
        return;
      }

      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copiato negli appunti!');
    } catch (err) {
      toast.error('Errore durante la condivisione');
      console.error('Failed to share:', err);
    } finally {
      setSharing(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Code className="w-5 h-5" />
          Condividi
        </CardTitle>
        <CardDescription>
          Integrare il widget di accessibilità nel tuo sito web o nelle tue pagine online mostra in modo chiaro e trasparente le informazioni di accessibilità della tua struttura. Rendi visibili queste informazioni e aiuta gli ospiti a capire subito se la tua struttura può rispondere alle loro esigenze specifiche prima della prenotazione.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Embed Mode Selection */}
        <div className="space-y-2">
          <Label htmlFor="embed-mode">Modalità</Label>
          <Select value={embedMode} onValueChange={(v: any) => setEmbedMode(v)}>
            <SelectTrigger id="embed-mode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="iframe">Iframe (consigliato)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Options Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Responsive Width Toggle */}
          <div className="space-y-2">
            <Label htmlFor="responsive-toggle" className="flex items-center gap-2">
              <input
                type="checkbox"
                id="responsive-toggle"
                checked={responsive}
                onChange={(e) => setResponsive(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300"
              />
              Larghezza responsive
            </Label>
            <p className="text-ss text-muted-foreground">
              Il widget si adatta alla larghezza del container
            </p>
          </div>

          {/* Auto Height Toggle */}
          <div className="space-y-2">
            <Label htmlFor="autoheight-toggle" className="flex items-center gap-2">
              <input
                type="checkbox"
                id="autoheight-toggle"
                checked={autoHeight}
                onChange={(e) => setAutoHeight(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300"
              />
              Altezza automatica
            </Label>
            <p className="text-ss text-muted-foreground">
              L'altezza si adatta al contenuto
            </p>
          </div>
        </div>

        {/* Height Input (fallback when autoHeight is off or initial height) */}
        <div className="space-y-2">
          <Label htmlFor="height-input">
            {autoHeight ? 'Altezza iniziale (px)' : 'Altezza fissa (px)'}
          </Label>
          <Input
            id="height-input"
            type="number"
            value={heightPx}
            onChange={(e) => setHeightPx(Number(e.target.value))}
            min={200}
            max={2000}
            step={50}
            disabled={false}
            className={autoHeight ? 'opacity-60' : ''}
          />
          {autoHeight && (
            <p className="text-ss text-muted-foreground">
              Valore usato come altezza iniziale, poi si adatta automaticamente
            </p>
          )}
        </div>

        {/* Generated Code */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="embed-code">Codice generato</Label>
            <span className="text-ss text-muted-foreground">
              {embedCode.length} caratteri
            </span>
          </div>
          <Textarea
            id="embed-code"
            value={embedCode}
            readOnly
            rows={12}
            className="font-mono text-ss"
          />
        </div>

        {/* Copy Button */}
        <Button
          onClick={handleCopy}
          className="w-full"
          variant={copied ? 'outline' : 'default'}
        >
          {copied ? (
            <>
              <Check className="w-4 h-4 mr-2" />
              Copiato!
            </>
          ) : (
            <>
              <Copy className="w-4 h-4 mr-2" />
              Copia codice widget
            </>
          )}
        </Button>

        <Button onClick={handleShare} variant="outline" className="w-full" disabled={sharing}>
          {sharing ? 'Condivisione...' : 'Condividi'}
        </Button>

        {/* Info Note */}
        <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-ss">
          <p className="text-blue-900">
            <strong>💡 Nota:</strong> Il codice generato utilizza le impostazioni di personalizzazione correnti (dimensione: <strong>{size}</strong>, tema: <strong>{theme}</strong>, lingua: <strong>{language}</strong>).
            Modifica le impostazioni nella tab "Anteprima" per aggiornare l'anteprima e il codice.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
